import { Heading, Text } from '@backend/lib-design-system';
import Image from 'next/image';
import { styled } from 'styled-components';

import { Column } from '~/components/Column';
import { Container } from '~/components/Container/Container';
import { useIsPromanBusinessUnit, useTranslation } from '~/hooks';
import { useBrandName } from '~/hooks/useBrandName';
import { getMediaQuery } from '~/utils/responsive/media';
import { sanitize } from '~/utils/sanitize';

const StyledBannerGrid = styled.div`
  display: flex;
  justify-content: center;

  ${getMediaQuery('mobile')} {
    flex-direction: column;
  }
`;

const StyledBannerGridItem = styled.div`
  display: flex;
  padding: 16px;
  flex: 1;
  gap: 20px;
  flex-direction: column;
  align-items: center;
`;

const StyledBannerGridItemContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 284px;
  text-align: center;
`;

// Proman's orange darkened to reach the 3:1 contrast WCAG asks of large text on white (#F58220 gives 2.6)
const BannerHeaderTitle = styled(Heading)<{ $isProman?: boolean }>`
  text-align: center;
  span {
    color: ${({ $isProman, theme }) => ($isProman ? '#E26D0A' : theme.colors.primary.normal)};
  }
`;

export const IziBanner = () => {
  const t = useTranslation();
  const brandName = useBrandName();
  const isProman = useIsPromanBusinessUnit();

  return (
    <Container>
      <Column>
        <BannerHeaderTitle
          variant="heading-2"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('banner.join_community', { name: brandName })),
          }}
        />
        <StyledBannerGrid>
          <StyledBannerGridItem>
            {!isProman && (
              <Image
                src="/images/icn-search.png"
                alt="search"
                width={48}
                height={44}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )}
            <StyledBannerGridItemContent>
              <Heading variant="heading-5" as="h3">
                {t('banner.large_choice')}
              </Heading>
              <Text variant="body-lighter">{t('banner.large_choice_desc')}</Text>
            </StyledBannerGridItemContent>
          </StyledBannerGridItem>
          {!isProman && (
            <StyledBannerGridItem>
              <Image
                src="/images/icn-fast.png"
                alt="fast"
                width={48}
                height={48}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
              <StyledBannerGridItemContent>
                <Heading variant="heading-5" as="h3">
                  {t('banner.simple_and_fast')}
                </Heading>
                <Text variant="body-lighter">{t('banner.simple_and_fast_desc')}</Text>
              </StyledBannerGridItemContent>
            </StyledBannerGridItem>
          )}
          <StyledBannerGridItem>
            {!isProman && (
              <Image
                src="/images/icn-checked.png"
                alt="checked"
                width={48}
                height={46}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )}
            <StyledBannerGridItemContent>
              <Heading variant="heading-5" as="h3">
                {t('banner.personal_service')}
              </Heading>
              <Text variant="body-lighter">{t('banner.personal_service_desc')}</Text>
            </StyledBannerGridItemContent>
          </StyledBannerGridItem>
        </StyledBannerGrid>
      </Column>
    </Container>
  );
};
